import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/api/client";
import { Loader2, FileBarChart2, Printer, CheckCircle2 } from "lucide-react";

export default function AdminGapReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [execSummary, setExecSummary] = useState("");
  const [dimNotes, setDimNotes] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/gap-report/latest");
      if (data.exists) {
        setReport(data);
        setExecSummary(data.editable_text?.executive_summary || "");
        setDimNotes(data.editable_text?.dimension_notes || {});
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true); setError("");
    try {
      const { data } = await api.post("/admin/gap-report/generate");
      setReport(data);
      setExecSummary("");
      setDimNotes(data.editable_text?.dimension_notes || {});
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setGenerating(false);
    }
  };

  const saveText = async () => {
    const { data } = await api.put(`/admin/gap-report/${report.report_id}`, { executive_summary: execSummary, dimension_notes: dimNotes });
    setReport(data);
  };

  const approve = async () => {
    const { data } = await api.post(`/admin/gap-report/${report.report_id}/approve`);
    setReport(data);
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-brand-accent" />;

  const d = report?.computed_data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-800">Informe de Brechas y Prioridades Formativas</h1>
          <p className="text-sm text-slate-500">Motor de reglas basado en diagnósticos finalizados. Ninguna afirmación es generada por IA.</p>
        </div>
        <button data-testid="generate-gap-report-btn" disabled={generating} onClick={generate} className="flex items-center gap-2 px-5 py-2.5 bg-brand-slate hover:bg-slate-900 text-white text-xs font-bold rounded-xl disabled:opacity-60">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileBarChart2 className="w-4 h-4" />} Generar informe diagnóstico y prioridades formativas
        </button>
      </div>

      {error && <div className="text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">{error}</div>}

      {!report ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-sm text-slate-500">Aún no se ha generado ningún informe.</div>
      ) : (
        <div className="space-y-5" data-testid="gap-report-content">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex items-center justify-between">
            <div>
              <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${report.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{report.status === "approved" ? "Aprobado" : "Borrador"}</span>
              <p className="text-xs text-slate-500 mt-2">Versión {report.version} · Corte: {d.cutoff_date || report.cutoff_date} · Instrumento {d.instrument_version}</p>
              <p className="text-xs font-bold text-slate-700 mt-1">Cobertura: {d.n_completed} / {d.total_matriculados} ({d.coverage_pct}%) {d.is_preliminary && <span className="text-amber-600">— PRELIMINAR (faltan diagnósticos)</span>}</p>
            </div>
            <button onClick={() => window.print()} className="no-print flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-600"><Printer className="w-4 h-4" /> Imprimir</button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h3 className="text-sm font-black text-slate-800 mb-2">Resumen ejecutivo (editable)</h3>
            <textarea data-testid="gap-report-exec-summary" rows={4} value={execSummary} onChange={(e) => setExecSummary(e.target.value)} disabled={report.status === "approved"} placeholder="Síntesis de fortalezas relativas y principales necesidades formativas..." className="w-full text-sm border border-slate-300 rounded-xl p-3 disabled:bg-slate-50" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h3 className="text-sm font-black text-slate-800 mb-4">Prioridades formativas (regla: % rojo desc., empate → promedio asc.)</h3>
            {d.priorities.length === 0 ? <p className="text-xs text-slate-400">Los datos no respaldan priorizar ninguna dimensión por sobre otra en este corte.</p> : (
              <div className="space-y-4">
                {d.recommendations.map((r) => (
                  <div key={r.dimension_id} className="border border-slate-200 rounded-xl p-4">
                    <p className="text-xs font-bold text-slate-800 mb-1">Hallazgo: {r.hallazgo}</p>
                    <p className="text-[11px] text-slate-600 mb-1"><b>Evidencia:</b> {r.evidencia}</p>
                    <p className="text-[11px] text-slate-600 mb-1"><b>Tema a profundizar:</b> {r.tema_a_profundizar}</p>
                    <p className="text-[11px] text-slate-600 mb-1"><b>Taller:</b> {r.taller}</p>
                    <p className="text-[11px] text-slate-600"><b>Práctica sugerida:</b> {r.practica_sugerida}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h3 className="text-sm font-black text-slate-800 mb-4">Resultados por dimensión</h3>
            <div className="space-y-4">
              {d.dimensions.map((dim) => (
                <div key={dim.id} className="border-b border-slate-100 pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-slate-800">{dim.title}</span>
                    <span className="text-xs font-black">{dim.avg_score} / 100 · {dim.pct_rojo}% rojo (n={dim.n})</span>
                  </div>
                  <p className="text-[10px] text-slate-500">Preguntas con menor frecuencia declarada: {dim.questions_attention.slice(0, 2).map((q) => `${q.id} (${q.avg_adjusted}/5)`).join(", ")}</p>
                  <textarea rows={2} placeholder="Nota editable del equipo docente para esta dimensión..." value={dimNotes[dim.id] || ""} onChange={(e) => setDimNotes({ ...dimNotes, [dim.id]: e.target.value })} disabled={report.status === "approved"} className="w-full text-xs border border-slate-200 rounded-lg p-2 mt-2 disabled:bg-slate-50" />
                </div>
              ))}
            </div>
          </div>

          {report.status !== "approved" && (
            <div className="flex gap-3">
              <button data-testid="gap-report-save-text-btn" onClick={saveText} className="px-5 py-2.5 border border-slate-300 rounded-xl text-xs font-bold text-slate-600">Guardar texto editable</button>
              <button data-testid="gap-report-approve-btn" onClick={approve} className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-xs font-bold flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Aprobar versión</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
